import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type {
    EstadoCivil,
    Genero,
    ProspectoDTO,
    ProspectoRequest,
    TipoPersona,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { toDate, toIsoDate } from '../../../utils/dates'
import { clip, onlyLetters, onlySimple } from '../../../utils/inputs'
import { errorMessage, PROSPECTS_PATH } from '../common/prospect-common'

export interface ProspectFormState {
    id: number | null
    personalidad: TipoPersona
    nombres: string
    sexo: Genero | ''
    fecNacimiento: Date | null
    edoCivil: EstadoCivil | ''
    telefono: string
    email: string
}

export interface ProspectFormProps {
    modal?: boolean
    prospecto?: Partial<ProspectoDTO>
    onCompleted?: (prospecto?: ProspectoDTO | null) => void
}

export interface FormMessage {
    type: 'error' | 'success'
    message: string
}

const LOAD_ERROR = 'No se pudo cargar el prospecto.'
const SAVE_ERROR = 'Error al guardar el prospecto'

const emptyState: ProspectFormState = {
    id: null,
    personalidad: 'fisica',
    nombres: '',
    sexo: '',
    fecNacimiento: null,
    edoCivil: '',
    telefono: '',
    email: '',
}

/** `react-phone-input-2` espera la lada incluida; el API guarda solo 10 dígitos. */
const toPhoneValue = (telefono?: string | null) => {
    const digits = String(telefono || '').replace(/\D/g, '')
    if (!digits) return ''
    return digits.length <= 10 ? `52${digits}` : digits
}

const toStoredPhone = (value: string) => {
    const digits = String(value || '').replace(/\D/g, '')
    if (!digits || digits === '52') return ''
    return (digits.startsWith('52') ? digits.slice(2) : digits).slice(0, 10)
}

const toFormState = (body: Partial<ProspectoDTO>): ProspectFormState => ({
    ...emptyState,
    id: body.id ?? null,
    personalidad: body.fiscal || 'fisica',
    nombres: body.nombres || '',
    sexo: body.sexo || '',
    fecNacimiento: toDate(body.fecNacimiento),
    edoCivil: body.edoCivil || '',
    email: body.email || '',
    telefono: toPhoneValue(body.telefono),
})

const toRequest = (state: ProspectFormState): ProspectoRequest => {
    const base = {
        id: state.id,
        nombres: state.nombres,
        fiscal: state.personalidad,
        telefono: toStoredPhone(state.telefono),
        email: state.email,
    }
    if (state.personalidad !== 'fisica') return base
    return {
        ...base,
        fecNacimiento: toIsoDate(state.fecNacimiento),
        edoCivil: state.edoCivil || null,
        sexo: state.sexo || null,
    }
}

export const useProspectFormModel = ({ modal, prospecto, onCompleted }: ProspectFormProps) => {
    const history = useHistory()
    const location = useLocation()
    const { prospectoId } = useParams()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || PROSPECTS_PATH

    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [state, setState] = useState<ProspectFormState>(emptyState)

    useEffect(() => {
        const parametroProspectoId = prospectoId || prospecto?.id
        if (!parametroProspectoId) {
            if (prospecto) setState(toFormState(prospecto))
            return undefined
        }
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const response: WrapperResponse<ProspectoDTO> | null = await (await APIInvoke.invokeGET(`/prospectos/${parametroProspectoId}`)).json()
                if (!active) return
                if (!response?.ok || !response.body) throw new Error(response?.message || LOAD_ERROR)
                setState(toFormState(response.body))
            } catch (error) {
                console.error(error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [prospectoId, prospecto])

    const patch = (partial: Partial<ProspectFormState>) => setState((current) => ({ ...current, ...partial }))
    const esFisica = state.personalidad === 'fisica'

    const change = {
        personalidad: (value: TipoPersona) => patch({ personalidad: value }),
        nombres: (value: string) => patch({ nombres: esFisica ? onlyLetters(value, 80) : onlySimple(value, 80) }),
        fecNacimiento: (value: string) => patch({ fecNacimiento: toDate(value) }),
        sexo: (value: Genero | '') => patch({ sexo: value }),
        edoCivil: (value: EstadoCivil | '') => patch({ edoCivil: value }),
        email: (value: string) => patch({ email: clip(value, 100) }),
        telefono: (value: string) => patch({ telefono: value }),
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        try {
            setLoading(true)
            setMessage(null)
            const request = toRequest(state)
            const response: WrapperResponse<ProspectoDTO> = state.id
                ? await (await APIInvoke.invokePUT(`/prospectos/${prospectoId || state.id}`, request)).json()
                : await (await APIInvoke.invokePOST('/prospectos', request)).json()

            if (response.ok && response.body) {
                const saved = response.body
                setState((current) => ({ ...current, id: saved.id }))
                setMessage({ type: 'success', message: 'Prospecto guardado correctamente' })
                if (onCompleted) onCompleted(saved)
                else history.push({ pathname: PROSPECTS_PATH, state: { detailId: saved.id, backUrl: PROSPECTS_PATH } })
            } else {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
            }
        } catch (error) {
            console.error('error al guardar el prospecto', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setLoading(false)
        }
    }

    const cancel = () => {
        if (modal) onCompleted?.()
        else history.push(backUrl)
    }

    return {
        form: state,
        esFisica,
        fecNacimientoValue: toIsoDate(state.fecNacimiento) || '',
        loading,
        busyLabel: prospectoId && !state.id ? 'Cargando prospecto…' : 'Guardando prospecto…',
        message,
        title: state.id ? 'Editar prospecto' : 'Nuevo prospecto',
        isEdit: Boolean(state.id),
        backTo: modal ? undefined : backUrl,
        change,
        save,
        cancel,
    }
}
